import { cn } from "@/lib/utils";
import type { ComplianceStatus } from "./status-pill";

/**
 * Tone names are the source design's own (`indigo` is really the brand tint —
 * kept as-is so the mapping stays traceable to `Visual - Demo.dc.html`).
 */
export type AvatarTone = "indigo" | "amber" | "slate";

const TONES: Record<AvatarTone, string> = {
  indigo: "bg-brand-tint text-brand",
  amber: "bg-warn-bg text-warn",
  slate: "bg-subtle text-ink-prose",
};

/** A crew member's compliance state picks their avatar tone. */
export function toneFor(status: ComplianceStatus): AvatarTone {
  if (status === "valid") return "indigo";
  if (status === "expiring") return "amber";
  return "slate";
}

/**
 * The 40px rounded-square initials chip used in every crew row and table.
 * Font size tracks the box at 0.36× — 40px → 14.4px, same as the source.
 */
export function CrewAvatar({
  initials,
  tone = "indigo",
  size = 40,
  className,
}: {
  initials: string;
  tone?: AvatarTone;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-tile border-[1.5px] border-border font-display font-bold",
        TONES[tone],
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </span>
  );
}
